import React, { FC, SyntheticEvent, useEffect, useRef, useState } from "react";

import {
  Alignment,
  Button,
  Card,
  Dialog,
  FaIcon,
  FaIcons,
  IconButton,
  Tag,
  useMediaQuery
} from "@in/component-library";
import { sendSiteimproveEvent } from "../../../utils/sendSiteimproveEvent";
import { dismissTask } from "../../apis/dismiss-task";
import { TasksAndServiceProspectTags } from "../../models/tasks-and-service-prospect-tags";
import CircleProgressbar from "../circle-progressbar/circle-progressbar";

export interface Action {
  actionType: "DismissTask" | "DeleteTask";
  actionText: string;
  iconName: string;
}

const handleDelete = async () => {
  /** For future implementation **/
};

interface TaskCardProps {
  id: string;
  title: string;
  subTitle?: string;
  deadline?: string | null;
  imageUrl?: string;
  primaryAction?: () => void | null;
  url?: string | null;
  primaryActionButtonText?: string;
  actions?: Action[];
  completionPct?: number | null;
  tags?: TasksAndServiceProspectTags[];

  timeLeftText?: string;
  completedLabel?: string;
  closeLabel?: string;
  category?: string;
  buttonIcon?: boolean;
}

export const TaskCard: FC<TaskCardProps> = ({
  id,
  title,
  subTitle,
  deadline,
  imageUrl,
  primaryAction,
  url,
  primaryActionButtonText,
  actions,
  completionPct,
  tags,
  timeLeftText,
  completedLabel,
  closeLabel,
  category,
  buttonIcon
}) => {
  const [isOpen, setIsOpen] = useState<boolean>();
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);
  const [visible, setVisible] = useState<boolean>(true);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const buttonGroupRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const menuButtonNeedFocus = useRef<boolean>(false);

  const [isMobile, setIsMobile] = useState<boolean>();
  const mediaQueryMobile = useMediaQuery("(max-width: 48em)");
  const mobileMatch = mediaQueryMobile ? mediaQueryMobile.matches : undefined;

  const handleDismiss = async (taskId: string) => {
    try {
      await dismissTask(taskId);
      setVisible(false);
    } catch {
      console.log("Failed to dismiss task");
    }
    sendSiteimproveEvent(`MyPage-MyTasks-${category}`, "DismissTask", title);
  };

  useEffect(() => {
    if (mobileMatch !== undefined && mobileMatch !== isMobile) {
      setIsMobile(mobileMatch);
    }
  }, [mobileMatch, isMobile]);

  useEffect(() => {
    const handleClickOutsideMobile = (event: MouseEvent | TouchEvent) => {
      const targetElement =
        "touches" in event ? (event.touches[0].target as HTMLElement) : (event.target as HTMLElement);

      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(targetElement) &&
        buttonGroupRef.current &&
        !buttonGroupRef.current.contains(targetElement)
      ) {
        setIsOpen(false);
        setActiveItemIndex(null);
      }
    };

    document.addEventListener("touchstart", handleClickOutsideMobile);
    document.addEventListener("mousedown", handleClickOutsideMobile);

    return () => {
      document.removeEventListener("touchstart", handleClickOutsideMobile);
      document.removeEventListener("mousedown", handleClickOutsideMobile);
    };
  }, [dropdownRef, buttonGroupRef, isOpen]);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else if (isOpen !== undefined) {
      dialogRef.current?.close();
      setActiveItemIndex(null);
      menuButtonNeedFocus.current = true;
    }
  }, [isOpen, isMobile]);

  const handleMenuButtonClick = (event: SyntheticEvent) => {
    event.preventDefault();
    setIsOpen(!isOpen);
    if (activeItemIndex === null) {
      setActiveItemIndex(0);
    } else {
      setActiveItemIndex(null);
    }
  };

  // Keyboard navigation
  const handleKeydown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (isOpen && dropdownRef.current) {
      const items = dropdownRef.current.children;
      const itemsLength = items.length;

      switch (event.key) {
        case "Tab":
          event.preventDefault();
          if (!event.shiftKey) {
            setActiveItemIndex((prev) => {
              if (prev === null) {
                return 0;
              } else if (prev < itemsLength - 1) {
                return prev + 1;
              } else {
                setIsOpen(false);
                menuButtonNeedFocus.current = true;
                return null;
              }
            });
          } else {
            setActiveItemIndex((prev) => {
              if (prev === null) {
                return itemsLength - 1;
              } else if (prev > 0) {
                return prev - 1;
              } else {
                setIsOpen(false);
                menuButtonNeedFocus.current = true;
                return null;
              }
            });
          }
          break;
        case "Escape":
          setIsOpen(false);
          setActiveItemIndex(null);
          menuButtonNeedFocus.current = true;
          break;
        default:
          break;
      }
    }
  };

  // Focus management
  useEffect(() => {
    let animationFrameListitem: number | null = null;
    let animationFrameMenuButton: number | null = null;
    const itemList = dropdownRef.current;
    const items =
      itemList &&
      Array.from(itemList.children)
        .map((li) => li.firstChild as HTMLButtonElement)
        .filter((button) => button);
    const menuButton = buttonGroupRef.current && (buttonGroupRef.current.firstChild as HTMLButtonElement);

    // Set focus on the active list item
    if (isOpen && items && activeItemIndex !== null) {
      const activeListItem = items[activeItemIndex];

      animationFrameListitem = window.requestAnimationFrame(() => {
        activeListItem && activeListItem.focus();
      });
    }
    // Set focus on the menu button if the menu has just been closed
    else if (menuButton && menuButtonNeedFocus.current) {
      animationFrameMenuButton = window.requestAnimationFrame(() => {
        menuButton && menuButton.focus();
      });
      menuButtonNeedFocus.current = false;
    }

    return () => {
      animationFrameListitem && window.cancelAnimationFrame(animationFrameListitem);
      animationFrameMenuButton && window.cancelAnimationFrame(animationFrameMenuButton);
    };
  }, [activeItemIndex, isOpen]);

  const actionsItemsList = isOpen && (
    <ul className="task-card__action-items" ref={dropdownRef} role="menu">
      {actions?.map((action, index) => (
        <li role="menuitem" className="task-card__action-item" key={index}>
          <Button
            className="task-card__action-button"
            theme="none"
            variant="plain"
            onClick={async () => {
              switch (action.actionType) {
                case "DeleteTask":
                  handleDelete();
                  break;
                case "DismissTask":
                  await handleDismiss(id);
                  break;
              }
              setIsOpen(false);
            }}
          >
            <FaIcon name={action.iconName} size="sm" className="task-card__action-button-icon" />
            {action.actionText}
          </Button>
        </li>
      ))}
      {isMobile && (
        <li className="task-card__action-item" role="menuitem">
          <Button
            className="task-card__action-button"
            theme="none"
            variant="plain"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            <FaIcon name={FaIcons.TimesLight} size="sm" className="task-card__action-button-icon" />
            {closeLabel}
          </Button>
        </li>
      )}
    </ul>
  );

  const actionsDropdown = (
    <div className="task-card__action-dropdown" onKeyDown={handleKeydown}>
      <div ref={buttonGroupRef}>
        <IconButton
          iconName={FaIcons.EllipsisH}
          size="medium"
          className="task-card__action-menu"
          onClick={handleMenuButtonClick}
          ariaLabel="Velg handling"
          aria-expanded={isOpen}
          aria-haspopup="menu"
        />
      </div>
      {isMobile ? (
        <Dialog title="" ref={dialogRef} verticalAlignment={Alignment.Bottom}>
          {actionsItemsList}
        </Dialog>
      ) : (
        actionsItemsList
      )}
    </div>
  );

  return visible ? (
    <Card boxShadow className="task-card">
      {imageUrl && (
        <div className="task-card__image-container">
          <img className="task-card__image" src={imageUrl} alt={title} />
        </div>
      )}
      <div className={"task-card__wrapper" + (completionPct ? " task-card__wrapper--course" : "")}>
        <div className="task-card__text">
          <div className="task-card__actions-mobile">
            <h3 className="task-card__title">{title}</h3>
            {tags && isMobile ? actionsDropdown : null}
          </div>

          {tags && (
            <div className="task-card__tags-mobile">
              {tags?.map((tag, index) => (
                <Tag className="task-card__tag" type={tag.tagStyle} key={index}>
                  {tag.tagId}
                </Tag>
              ))}
            </div>
          )}
          <p className="task-card__sub-title">{subTitle}</p>

          {deadline && (
            <div className="task-card__deadline">
              <span>
                <FaIcon name={FaIcons.CalendarRegular} className="task-card__deadline-icon" size="sm" />
              </span>
              <span className="task-card__deadline-text">{deadline}</span>
              <span>{timeLeftText && <Tag type="none">{timeLeftText}</Tag>}</span>
            </div>
          )}
        </div>

        {completionPct !== null && completionPct !== undefined && (
          <div className="task-card__completion">
            <CircleProgressbar percentage={completionPct} />

            <div className="task-card__completion-text">
              <p className="task-card__completion-percentage">{`${completionPct}%`}</p>
              <p>{completedLabel}</p>
            </div>
          </div>
        )}

        <div className="task-card__action">
          {tags && (
            <div className="task-card__tags">
              {tags?.map((tag, index) => (
                <Tag className="task-card__tag" type={tag.tagStyle} key={index}>
                  {tag.tagId}
                </Tag>
              ))}

              {!isMobile ? actionsDropdown : null}
            </div>
          )}

          <Button
            type="button"
            onClick={() => {
              sendSiteimproveEvent(
                `MyPage-MyTasks-${category}`,
                primaryActionButtonText ? primaryActionButtonText : "ButtonClicked",
                title
              );
              if (primaryAction) {
                primaryAction();
              } else if (url) {
                window.location.href = url; // Redirects to the URL
              }
            }}
            variant={completionPct && completionPct > 0 ? "outlined" : "solid"}
          >
            {/* <FaIcon name={FaIcons.EditLight} /> */}
            {primaryActionButtonText}
            {buttonIcon && <FaIcon className="button-icon" name={FaIcons.ArrowRight} />}
          </Button>
        </div>
      </div>
    </Card>
  ) : (
    <></>
  );
};

export default TaskCard;
